import { useApiClient, WebDisMetadata } from '@m43/core';
import {
    EventPanelForm,
    EventPanelFormType,
    EventPanelResource,
    EventPanelView,
} from '@mark43/cad-api';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { v4 } from 'uuid';
import { useActionBroadcaster } from '../../action-broadcaster/use-action-broadcaster';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useGenerateReportingEventNumber } from '../../common/hooks/use-generate-reporting-event-number';
import { useDexMetadata } from '../../data-exchange/use-dex-metadata';
import { ElectronDexMetadata } from '../../electron-bridge/electron-bridge';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { countDirtyFields } from '../count-dirty-fields';
import { filterInvalidEventPanelEntities } from '../event-panel-form/filter-invalid-event-panel-entities';
import { getEventButtonGuardBooleans } from '../event-panel-form/helpers/button-guards';
import { EventPanelMetadataStore } from '../event-state/event-panel-metadata-store';
import { useGenerateEventPDF } from './use-generate-event-pdf';
import { SESSIONS_INCIDENT_IDS } from '../event-panel-form/constants';
import type { Discipline } from '../event-panel-form/helpers/pro-qa-buttons-helpers';
import { isTemporaryIncidentId } from '../event-panel-form/helpers/pro-qa-buttons-helpers';

type EventApiProps = {
    onSaveEvent?: (
        eventPanelView: EventPanelView[],
        saveContext: {
            isProQaRelated: boolean;
        }
    ) => void;
    onClearEvent?: () => void;
    onUnassignCurrentUserUnit?: () => void;
    eventMetadataStore: EventPanelMetadataStore;
    eventForm: UseFormReturn<EventPanelFormType, any, any>;
};

export function useEventApi(props: EventApiProps) {
    const apiClient = useApiClient();
    const { dexMetadata } = useDexMetadata();
    const { trigger } = useActionBroadcaster();
    const { eventPanelConfiguration } = useBootstrapState();
    const { isFirstResponder } = useApplicationType();

    const { eventMetadataStore, eventForm } = props;

    const saveEventMutation = useMutation(
        (eventPanelForm: EventPanelFormType) => {
            const url = isFirstResponder ? 'event_panel/first_responder' : 'event_panel/';

            return apiClient.request<
                EventPanelResource.SaveEventPanel | EventPanelResource.SaveEventPanelFirstResponder
            >({
                method: 'PUT',
                url,
                data: eventPanelForm as EventPanelForm,
                timeout: 10000,
            });
        },
        {
            onSuccess(data, eventPanelFormType) {
                const eventPanelViews = data.data.data?.eventPanelViews;
                if (eventPanelViews) {
                    props.onSaveEvent?.(eventPanelViews, {
                        isProQaRelated: !!eventPanelFormType.proQaTemporaryIncidentId?.trim(),
                    });
                }
            },
        }
    );

    const getPendingProQaTempId = () => {
        const stored = storageHelpers.getSessionItem<Record<Discipline, string | undefined>>(
            SESSIONS_INCIDENT_IDS
        );
        if (!stored) {
            return undefined;
        }

        return Object.values(stored)
            .map((value) => value?.trim())
            .find((value): value is string => isTemporaryIncidentId(value));
    };

    const onSubmit = useCallback(
        (formValues: EventPanelFormType) => {
            if (saveEventMutation.isLoading) {
                return;
            }
            const sanitizedValues = filterInvalidEventPanelEntities(formValues);
            if (!sanitizedValues.eventId && !sanitizedValues.proQaTemporaryIncidentId) {
                sanitizedValues.proQaTemporaryIncidentId = getPendingProQaTempId() ?? null;
            }
            return saveEventMutation.mutateAsync({
                ...sanitizedValues,
                deviceName:
                    (dexMetadata as ElectronDexMetadata)?.deviceName ??
                    (dexMetadata as WebDisMetadata)?.machineName ??
                    '',
            });
        },
        [saveEventMutation, dexMetadata]
    );

    const saveEvent = useCallback(() => {
        const eventCommunicationSave = eventPanelConfiguration?.eventCommunicationSave;
        if (eventCommunicationSave) {
            eventForm.setValue('eventCommunication', eventCommunicationSave);
            const dirtyFieldCount = countDirtyFields(eventForm.formState.dirtyFields);
            if (dirtyFieldCount > 0) {
                eventForm.handleSubmit(onSubmit)();
            }
        }
    }, [eventForm, eventPanelConfiguration, onSubmit]);

    const transmitEvent = useCallback(() => {
        const eventCommunicationEmergency = eventPanelConfiguration?.eventCommunicationEmergency;
        if (eventCommunicationEmergency) {
            eventForm.setValue('eventCommunication', eventCommunicationEmergency);
            eventForm.handleSubmit(onSubmit)();
        }
    }, [eventForm, eventPanelConfiguration, onSubmit]);

    const unassignCurrentUserUnit = useCallback(() => {
        const { isClearedEvent, hasDirtyFields } = getEventButtonGuardBooleans(
            eventMetadataStore,
            eventForm
        );

        if (isClearedEvent || hasDirtyFields) {
            return;
        }

        props.onUnassignCurrentUserUnit?.();
    }, [eventForm, eventMetadataStore, props]);

    const clearEvent = useCallback(() => {
        const { eventExists, isClearedEvent, hasDirtyFields } = getEventButtonGuardBooleans(
            eventMetadataStore,
            eventForm
        );

        if (isClearedEvent) {
            return;
        }

        if (!eventExists && !isFirstResponder) {
            trigger([
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'FOCUS_PRIMARY_DISPOSITION',
                },
            ]);
            return;
        }
        if (!hasDirtyFields) {
            props.onClearEvent?.();
        }
    }, [eventForm, eventMetadataStore, isFirstResponder, props, trigger]);

    const { generatePDFWithoutAttachments } = useGenerateEventPDF();
    const generatePDF = useCallback(() => {
        const { hasDirtyFields, isActiveAgencyEvent, isClearedEvent } = getEventButtonGuardBooleans(
            eventMetadataStore,
            eventForm
        );

        if (isClearedEvent || (isActiveAgencyEvent && !hasDirtyFields)) {
            const { eventId, agencyEventNumber } = eventMetadataStore.state.metadata ?? {};
            if (agencyEventNumber !== undefined && eventId !== undefined) {
                generatePDFWithoutAttachments(eventId, agencyEventNumber);
            }
        }
    }, [eventForm, eventMetadataStore, generatePDFWithoutAttachments]);

    const [
        generateReportingEventNumber,
        { isLoading: isGeneratingRen },
    ] = useGenerateReportingEventNumber();

    const generateRen = useCallback(() => {
        const {
            hasDirtyFields,
            isActiveAgencyEvent,
            isClearedEvent,
            showGenerateRenPrimaryAgencyButton,
        } = getEventButtonGuardBooleans(eventMetadataStore, eventForm);

        if (
            !isGeneratingRen &&
            showGenerateRenPrimaryAgencyButton &&
            (isClearedEvent || (isActiveAgencyEvent && !hasDirtyFields))
        ) {
            const { agencyId, eventId, agencyEventNumber } =
                eventMetadataStore.state.metadata ?? {};
            const numberOfRensForPrimaryAgency = agencyId
                ? (
                      eventMetadataStore.state.metadata?.agencyIdToReportingEventNumbers?.[
                          agencyId
                      ] ?? []
                  ).length
                : 0;
            if (agencyEventNumber !== undefined && eventId !== undefined) {
                generateReportingEventNumber({
                    agencyEventId: eventId,
                    agencyEventNumber,
                    numberOfRensForPrimaryAgency,
                });
            }
        }
    }, [eventForm, eventMetadataStore, generateReportingEventNumber, isGeneratingRen]);

    return {
        saveEventMutation,
        saveEvent,
        transmitEvent,
        unassignCurrentUserUnit,
        clearEvent,
        onSubmit,
        generatePDF,
        generateRen,
    };
}
